import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Tratamente from "./Tratamente";
import TratamenteList from "./TratamenteList";
import Button from "../ui/Button";
import Modal from "../ui/Modal";
import FinancialHistoryModal from "../patients/FinancialHistoryModal";
import { useTratamente } from "../../context/TratamenteContext";

const normalizeEmailValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeIdValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const ensureMedicName = (medic, fallbackName) => {
  const candidates = [
    medic?.name,
    medic?.fullName,
    medic?.displayName,
    medic?.lastName && medic?.firstName
      ? `${medic.lastName} ${medic.firstName}`
      : "",
  ];
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue;
    const trimmed = candidate.trim();
    if (!trimmed) continue;
    const looksLikeEmail = trimmed.includes("@");
    const looksLikeId = /^[0-9a-f]{24}$/i.test(trimmed);
    if (!looksLikeEmail && !looksLikeId) {
      return trimmed;
    }
  }
  return fallbackName;
};

const ensureMedicShape = (medic, fallbackName = "Medic necunoscut") => {
  if (!medic || typeof medic !== "object") return null;
  const id = typeof medic.id === "string" ? medic.id : "";
  const email = typeof medic.email === "string" ? medic.email : "";
  const normalizedEmail =
    typeof medic.normalizedEmail === "string" && medic.normalizedEmail
      ? medic.normalizedEmail
      : normalizeEmailValue(email);
  const normalizedId =
    typeof medic.normalizedId === "string" && medic.normalizedId
      ? medic.normalizedId
      : normalizeIdValue(id);
  const name = ensureMedicName(medic, email || fallbackName);
  return { id, email, name, normalizedEmail, normalizedId };
};

export default function TratamenteModal({
  patient,
  onClose,
  treatmentsOverride = null,
  commissionMeta = null,
  commissionLoading = false,
}) {
  const [showEditor, setShowEditor] = useState(false);
  const [editingTooth, setEditingTooth] = useState(null);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const { tratamente, ensureLoaded, loadingPatients, notifyPlanCompletion } =
    useTratamente();

  useEffect(() => {
    if (patient?.id) {
      ensureLoaded(patient.id).catch(() => {});
    }
  }, [patient?.id, ensureLoaded]);

  if (!patient) return null;

  const formatName = (name) =>
    name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : "";

  const pacientTratamente = Array.isArray(tratamente?.[patient.id])
    ? tratamente[patient.id]
    : [];
  const loading = loadingPatients?.[patient.id];
  const hasExistingPlan = pacientTratamente.length > 0;
  const treatmentsForModal = useMemo(
    () =>
      Array.isArray(treatmentsOverride) && treatmentsOverride.length
        ? treatmentsOverride
        : pacientTratamente,
    [treatmentsOverride, pacientTratamente]
  );

  const defaultMedic = useMemo(() => {
    const email = typeof patient?.medicEmail === "string" ? patient.medicEmail : "";
    const name =
      patient?.medicName ||
      patient?.assignedDoctor ||
      email ||
      "Medic necunoscut";
    const id =
      typeof patient?.medicId === "string" ? patient.medicId : "";
    if (!email && !name) return null;
    return ensureMedicShape({ id, email, name });
  }, [patient?.medicEmail, patient?.medicName, patient?.assignedDoctor, patient?.medicId]);

  const effectiveCommissionMeta = useMemo(() => {
    const baseMeta = commissionMeta || {};
    const medicsListRaw = Array.isArray(baseMeta.medics)
      ? baseMeta.medics
      : defaultMedic
        ? [defaultMedic]
        : [];
    const medics = medicsListRaw
      .map((medic) => ensureMedicShape(medic, defaultMedic?.name))
      .filter(Boolean);

    const referrer =
      ensureMedicShape(baseMeta.referrer, defaultMedic?.name) ||
      defaultMedic ||
      null;

    const resolveOwner =
      typeof baseMeta.resolveOwner === "function"
        ? baseMeta.resolveOwner
        : (treatment) =>
            treatment?.doctorId ||
            treatment?.doctor ||
            referrer?.id ||
            referrer?.email ||
            "";

    return {
      medics,
      referrer,
      resolveOwner,
    };
  }, [commissionMeta, defaultMedic]);

  const effectiveCommissionLoading = commissionLoading || Boolean(loading);

  const handleManualCompletionNotice = async () => {
    if (!patient?.id) return;
    setNotifyLoading(true);
    try {
      const message = await notifyPlanCompletion(patient.id);
      toast.success(message || "Notificarea a fost trimisă către medic.");
    } catch (error) {
      console.error("Nu am putut trimite notificarea de finalizare:", error);
      toast.error(
        error?.message || "Nu am putut trimite notificarea de finalizare."
      );
    } finally {
      setNotifyLoading(false);
    }
  };

  return (
    <Modal
      title={null}
      onClose={onClose}
      showClose
      size="xl"
      contentClassName="!max-w-6xl h-[90vh] overflow-y-auto"
    >
      <div className="flex flex-col gap-4 h-full">
        <div className="flex flex-wrap justify-between items-center gap-3 border-b pb-3 mb-2 pr-12">
          <h3 className="font-bold text-lg flex items-center gap-2">
            🦷 Plan tratamente – {formatName(patient.lastName)} {formatName(patient.firstName)}
          </h3>
          {!showEditor && hasExistingPlan && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  setEditingTooth(null);
                  setShowEditor(true);
                }}
              >
                ✏️ Modifică planul
              </Button>
              <Button
                variant="success"
                onClick={handleManualCompletionNotice}
                disabled={notifyLoading}
              >
                {notifyLoading ? "Se trimite..." : "✅ Terminat"}
              </Button>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-700 space-y-1">
          <p>
            <strong>CNP:</strong> {patient.cnp || "-"}
          </p>
          <p>
            <strong>Telefon:</strong> {patient.phone || "-"}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Se încarcă planul de tratamente...
            </div>
          ) : showEditor ? (
            <Tratamente
              patientId={patient.id}
              editingTooth={editingTooth}
              clearEditing={() => {
                setEditingTooth(null);
                setShowEditor(false);
              }}
            />
          ) : (
            <TratamenteList
              patient={patient}
              onEditTooth={(tooth) => {
                setEditingTooth(tooth);
                setShowEditor(true);
              }}
              onAddToPlan={() => {
                setEditingTooth(null);
                setShowEditor(true);
              }}
              onFinancial={() => setShowFinancialModal(true)}
            />
          )}
        </div>
      </div>
      {showFinancialModal && (
        <FinancialHistoryModal
          patientId={patient.id}
          records={Array.isArray(patient.financialRecords) ? patient.financialRecords : []}
          patientName={`${formatName(patient.lastName)} ${formatName(patient.firstName)}`.trim()}
          treatments={treatmentsForModal}
          commissionMeta={effectiveCommissionMeta}
          commissionLoading={effectiveCommissionLoading}
          onClose={() => setShowFinancialModal(false)}
        />
      )}
    </Modal>
  );
}
